<template>
  <div class="profile-page">
    <div class="profile-container">
      <div class="profile-header">
        <div class="profile-avatar" v-if="user && user.uPic">
          <img :src="user.uPic" alt="avatar" />
        </div>
        <div class="profile-avatar profile-avatar-default" v-else>
          {{ user && user.uName ? user.uName.charAt(0).toUpperCase() : 'U' }}
        </div>
        <div class="profile-info">
          <h1 class="profile-name">{{ user && user.uName }}</h1>
          <p class="profile-email">{{ user && user.email }}</p>
          <p class="profile-join-time">{{ $t('profile.joinTime') }} {{ joinTime }}</p>
        </div>
      </div>

      <div class="profile-tabs">
        <button 
          v-for="tab in tabs" 
          :key="tab.key"
          :class="['tab-btn', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          <span v-html="tab.icon"></span>
          {{ tab.label }}
        </button>
      </div>

      <div class="profile-content">
        <div v-if="activeTab === 'account'" class="tab-content">
          <div class="card" v-if="!lastPay">
            <div class="card-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/></svg>
              {{ $t('profile.balance.title') }}
            </div>
            <div class="card-body">
              <div class="balance-info">
                <div class="balance-label">{{ $t('profile.balance.available') }}</div>
                <div class="balance-value">{{ formatNumber(balance) }}</div>
              </div>
            </div>
          </div>

          <div class="card subscription-card" v-if="lastPay">
            <div class="card-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/></svg>
              {{ $t('profile.balance.title') }}
            </div>
            <div class="card-body">
              <div class="balance-info">
                <div class="balance-label">{{ $t('profile.balance.available') }}</div>
                <div class="balance-value">{{ formatNumber(balance) }}</div>
              </div>
              <div class="subscription-info">
                <div class="subscription-grid">
                  <div class="subscription-item">
                    <div class="subscription-label">{{ $t('profile.subscription.currentPlan') }}</div>
                    <div class="subscription-value">{{ getPlanDisplayName(lastPay.planName) }}</div>
                  </div>
                  <div class="subscription-item">
                    <div class="subscription-label">{{ $t('profile.subscription.status') }}</div>
                    <div class="subscription-value" :class="subscriptionStatusClass">
                      <svg v-if="lastPay.state === 'finished'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                      <svg v-else-if="lastPay.state === 'cancel'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
                      <svg v-else-if="lastPay.state === 'pending'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
                      <svg v-else-if="lastPay.state === 'fail'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></svg>
                      {{ subscriptionStatusText }}
                    </div>
                  </div>
                  <div class="subscription-item">
                    <div class="subscription-label">{{ $t('profile.subscription.expiryDate') }}</div>
                    <div class="subscription-value">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                      {{ subscriptionExpiryDate }}
                    </div>
                  </div>
                </div>
                <div class="subscription-actions">
                  <router-link to="/pricing" class="subscription-btn upgrade-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"></path><path d="M7 17 17 7"></path></svg>
                    {{ $t('profile.subscription.upgradePlan') }}
                  </router-link>
                </div>
                <div class="subscription-cancel" v-if="lastPay.state === 'finished'">
                  <button class="cancel-btn" @click="cancelSubscription">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></svg>
                    {{ $t('profile.subscription.cancelPlan') }}
                  </button>
                  <p class="cancel-note">{{ $t('profile.subscription.cancelNote') }}</p>
                </div>
                <div class="subscription-cancel" v-else-if="lastPay.state === 'cancel'">
                  <div class="cancel-warning">
                    <p>{{ $t('profile.subscription.cancelWarning') }} <strong>{{ subscriptionExpiryDate }}</strong></p>
                  </div>
                  <button class="restore-btn" @click="restoreSubscription">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></svg>
                    {{ $t('profile.subscription.restorePlan') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="card upgrade-card" v-if="!lastPay">
            <div class="upgrade-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"/><path d="M5 21h14"/></svg>
            </div>
            <h3>{{ $t('profile.upgrade.title') }}</h3>
            <p>{{ $t('profile.upgrade.desc') }}</p>
            <router-link to="/pricing" class="upgrade-btn">{{ $t('profile.upgrade.btn') }}</router-link>
          </div>
        </div>

        <div v-if="activeTab === 'billing'" class="tab-content">
          <div class="card billing-card">
            <div class="card-header billing-header">
              <div class="billing-header-info">
                <div class="billing-title">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M12 7v5l4 2"></path></svg>
                  {{ $t('profile.billing.title') }}
                </div>
                <div class="billing-subtitle">{{ $t('profile.billing.subtitle') }}</div>
              </div>
              <button class="refresh-btn" @click="loadPaymentHistory(currentPage)" :disabled="paymentLoading">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></svg>
                {{ $t('profile.billing.refresh') }}
              </button>
            </div>
            <div class="card-body billing-body">
              <div v-if="paymentLoading" class="loading-text billing-empty">{{ $t('common.loading') }}</div>
              <div v-else-if="paymentList.length === 0" class="empty-text billing-empty">{{ $t('profile.billing.empty') }}</div>
              <div v-else class="billing-table-wrapper">
                <table class="billing-table">
                  <thead>
                    <tr>
                      <th>{{ $t('profile.billing.plan') }}</th>
                      <th>{{ $t('profile.billing.date') }}</th>
                      <th class="text-right">{{ $t('profile.billing.amount') }}</th>
                      <th class="text-right">{{ $t('profile.billing.invoice') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in paymentList" :key="item.payment_id">
                      <td>
                        <div class="plan-name">{{ getPlanDisplayName(item.planName) }}</div>
                        <div class="plan-type">{{ getPayTypeName(item.pay_type, item.pay_mode) }}</div>
                      </td>
                      <td class="date-cell">{{ formatDate(item.pay_date) }}</td>
                      <td class="amount-cell">${{ item.price }}</td>
                      <td class="invoice-cell">
                        <button class="invoice-btn" @click="downloadInvoice(item)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"></path><path d="M14 2v5a1 1 0 0 0 1 1h5"></path><path d="M12 18v-6"></path><path d="m9 15 3 3 3-3"></path></svg>
                          {{ $t('profile.billing.download') }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="totalPage > 1" class="pagination">
                <button 
                  class="page-btn" 
                  :disabled="currentPage <= 1" 
                  @click="loadPaymentHistory(currentPage - 1)"
                >
                  {{ $t('common.prev') }}
                </button>
                <span class="page-info">{{ currentPage }} / {{ totalPage }}</span>
                <button 
                  class="page-btn" 
                  :disabled="currentPage >= totalPage" 
                  @click="loadPaymentHistory(currentPage + 1)"
                >
                  {{ $t('common.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'privacy'" class="tab-content">
          <div class="privacy-card">
            <h2 class="privacy-title">{{ $t('profile.privacy.cardTitle') }}</h2>
            <p class="privacy-subtitle">{{ $t('profile.privacy.cardSubtitle') }}</p>
            <ul class="privacy-list">
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
                {{ $t('profile.privacy.item1') }}
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                {{ $t('profile.privacy.item2') }}
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {{ $t('profile.privacy.item3') }}
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
                {{ $t('profile.privacy.item4') }}
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
                {{ $t('profile.privacy.item5') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="card support-card">
        <div class="card-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          {{ $t('profile.support.title') }}
        </div>
        <div class="card-body support-body">
          <p>{{ $t('profile.support.desc') }}</p>
          <a href="mailto:miral_support@miralvis.com" class="support-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            {{ $t('profile.support.btn') }}
          </a>
          <p class="support-note">{{ $t('profile.support.note') }}</p>
        </div>
      </div>
    </div>

    <div v-if="showInvoiceModal" class="modal-overlay" @click.self="closeInvoiceModal">
      <div class="modal-content invoice-modal">
        <div class="modal-header">
          <h3>{{ $t('profile.billing.invoiceModal.title') }}</h3>
          <button class="modal-close" @click="closeInvoiceModal">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="invoice-section">
            <h4 class="section-title">{{ $t('profile.billing.invoiceModal.orderInfo') }}</h4>
            <div class="invoice-detail">
              <div class="invoice-row">
                <span class="invoice-label">{{ $t('profile.billing.invoiceModal.plan') }}</span>
                <span class="invoice-value">{{ selectedInvoice ? getPlanDisplayName(selectedInvoice.planName) : '' }}</span>
              </div>
              <div class="invoice-row">
                <span class="invoice-label">{{ $t('profile.billing.invoiceModal.date') }}</span>
                <span class="invoice-value">{{ selectedInvoice ? formatDate(selectedInvoice.pay_date) : '' }}</span>
              </div>
              <div class="invoice-row">
                <span class="invoice-label">{{ $t('profile.billing.invoiceModal.amount') }}</span>
                <span class="invoice-value">${{ selectedInvoice ? selectedInvoice.price : '' }}</span>
              </div>
            </div>
          </div>
          
          <div class="invoice-section">
            <h4 class="section-title">{{ $t('profile.billing.invoiceModal.invoiceInfo') }}</h4>
            <div class="invoice-form">
              <div class="form-row">
                <div class="form-group" :class="{ 'has-error': formErrors.fullName }">
                  <label>{{ $t('profile.billing.invoiceModal.fullName') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.fullName" :placeholder="$t('profile.billing.invoiceModal.fullNamePlaceholder')" />
                  <span v-if="formErrors.fullName" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
                <div class="form-group" :class="{ 'has-error': formErrors.email }">
                  <label>{{ $t('profile.billing.invoiceModal.email') }} <span class="required">*</span></label>
                  <input type="email" v-model="invoiceForm.email" :placeholder="$t('profile.billing.invoiceModal.emailPlaceholder')" />
                  <span v-if="formErrors.email" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group full">
                  <label>{{ $t('profile.billing.invoiceModal.company') }} <span class="optional">({{ $t('profile.billing.invoiceModal.optional') }})</span></label>
                  <input type="text" v-model="invoiceForm.company" :placeholder="$t('profile.billing.invoiceModal.companyPlaceholder')" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group full" :class="{ 'has-error': formErrors.taxId }">
                  <label>{{ $t('profile.billing.invoiceModal.taxId') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.taxId" :placeholder="$t('profile.billing.invoiceModal.taxIdPlaceholder')" />
                  <span v-if="formErrors.taxId" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group full" :class="{ 'has-error': formErrors.addressLine1 }">
                  <label>{{ $t('profile.billing.invoiceModal.addressLine1') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.addressLine1" :placeholder="$t('profile.billing.invoiceModal.addressLine1Placeholder')" />
                  <span v-if="formErrors.addressLine1" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group full">
                  <label>{{ $t('profile.billing.invoiceModal.addressLine2') }} <span class="optional">({{ $t('profile.billing.invoiceModal.optional') }})</span></label>
                  <input type="text" v-model="invoiceForm.addressLine2" :placeholder="$t('profile.billing.invoiceModal.addressLine2Placeholder')" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group" :class="{ 'has-error': formErrors.city }">
                  <label>{{ $t('profile.billing.invoiceModal.city') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.city" :placeholder="$t('profile.billing.invoiceModal.cityPlaceholder')" />
                  <span v-if="formErrors.city" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
                <div class="form-group" :class="{ 'has-error': formErrors.province }">
                  <label>{{ $t('profile.billing.invoiceModal.province') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.province" :placeholder="$t('profile.billing.invoiceModal.provincePlaceholder')" />
                  <span v-if="formErrors.province" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group" :class="{ 'has-error': formErrors.postalCode }">
                  <label>{{ $t('profile.billing.invoiceModal.postalCode') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.postalCode" :placeholder="$t('profile.billing.invoiceModal.postalCodePlaceholder')" />
                  <span v-if="formErrors.postalCode" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
                <div class="form-group" :class="{ 'has-error': formErrors.country }">
                  <label>{{ $t('profile.billing.invoiceModal.country') }} <span class="required">*</span></label>
                  <input type="text" v-model="invoiceForm.country" :placeholder="$t('profile.billing.invoiceModal.countryPlaceholder')" />
                  <span v-if="formErrors.country" class="error-text">{{ $t('profile.billing.invoiceModal.required') }}</span>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group full">
                  <label>{{ $t('profile.billing.invoiceModal.invoiceRemarks') }} <span class="optional">({{ $t('profile.billing.invoiceModal.optional') }})</span></label>
                  <input type="text" v-model="invoiceForm.invoiceRemarks" :placeholder="$t('profile.billing.invoiceModal.invoiceRemarksPlaceholder')" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn secondary" @click="closeInvoiceModal">
            {{ $t('profile.billing.invoiceModal.close') }}
          </button>
          <button class="modal-btn primary" @click="confirmDownloadInvoice">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
            {{ $t('profile.billing.invoiceModal.download') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentUser } from '../api/auth'
import { getPaymentHistory, generateInvoicePdf, paySubscription } from '../api/stripe'

export default {
  name: 'Profile',
  data() {
    return {
      activeTab: 'account',
      balance: 0,
      loading: false,
      paymentLoading: false,
      paymentList: [],
      currentPage: 1,
      totalPage: 1,
      pageSize: 5,
      lastPay: null,
      showInvoiceModal: false,
      selectedInvoice: null,
      invoiceForm: {
        fullName: '',
        email: '',
        company: '',
        taxId: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
        invoiceRemarks: ''
      },
      formErrors: {},
      privacySettings: {
        dataCollection: true,
        analytics: false
      }
    }
  },
  computed: {
    user() {
      var userInfo = localStorage.getItem('userInfo')
      if (userInfo) {
        return JSON.parse(userInfo)
      }
      return null
    },
    subscriptionExpiryDate() {
      if (!this.lastPay || !this.lastPay.pay_date) {
        return ''
      }
      var payDate = new Date(this.lastPay.pay_date)
      var payMode = this.lastPay.pay_mode
      if (payMode === '1') {
        payDate.setMonth(payDate.getMonth() + 1)
      } else if (payMode === '2') {
        return this.$t('profile.subscription.permanent')
      }
      return payDate.getFullYear() + '/' + (payDate.getMonth() + 1) + '/' + payDate.getDate()
    },
    subscriptionStatusClass() {
      if (!this.lastPay) return ''
      var state = this.lastPay.state
      if (state === 'finished') return 'status-active'
      if (state === 'cancel') return 'status-cancel'
      if (state === 'pending') return 'status-pending'
      if (state === 'fail') return 'status-fail'
      return ''
    },
    subscriptionStatusText() {
      if (!this.lastPay) return ''
      var state = this.lastPay.state
      if (state === 'finished') return this.$t('profile.subscription.activated')
      if (state === 'cancel') return this.$t('profile.subscription.cancelled')
      if (state === 'pending') return this.$t('profile.subscription.pending')
      if (state === 'fail') return this.$t('profile.subscription.failed')
      return ''
    },
    joinTime() {
      if (this.user && this.user.lastLoginTime) {
        var date = new Date(this.user.lastLoginTime)
        return date.getFullYear() + '年' + (date.getMonth() + 1) + '月'
      }
      return new Date().getFullYear() + '年' + (new Date().getMonth() + 1) + '月'
    },
    tabs() {
      return [
        { 
          key: 'account', 
          label: this.$t('profile.tabs.account'),
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
        },
        { 
          key: 'billing', 
          label: this.$t('profile.tabs.billing'),
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z"/></svg>'
        },
        { 
          key: 'privacy', 
          label: this.$t('profile.tabs.privacy'),
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>'
        }
      ]
    }
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 'billing' && this.paymentList.length === 0) {
        this.loadPaymentHistory(1)
      }
    }
  },
  methods: {
    formatNumber(n) {
      return n ? n.toLocaleString() : '0'
    },
    getPlanName(planName) {
      return planName
    },
    getPlanDisplayName(planName) {
      var planNames = {
        'basic': this.$t('plans.basic.name'),
        'professional': this.$t('plans.pro.name'),
        'ultra': this.$t('plans.ultimate.name')
      }
      return planNames[planName] || planName
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      var date = new Date(dateStr)
      return date.getFullYear() + '/' + String(date.getMonth() + 1).padStart(2, '0') + '/' + String(date.getDate()).padStart(2, '0') + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
    },
    downloadInvoice(item) {
      this.selectedInvoice = item
      this.formErrors = {}
      this.invoiceForm = {
        fullName: this.user && this.user.uName ? this.user.uName : '',
        email: this.user && this.user.email ? this.user.email : '',
        company: '',
        taxId: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
        invoiceRemarks: ''
      }
      this.showInvoiceModal = true
    },
    validateForm() {
      var errors = {}
      if (!this.invoiceForm.fullName.trim()) {
        errors.fullName = true
      }
      if (!this.invoiceForm.email.trim()) {
        errors.email = true
      }
      if (!this.invoiceForm.taxId.trim()) {
        errors.taxId = true
      }
      if (!this.invoiceForm.addressLine1.trim()) {
        errors.addressLine1 = true
      }
      if (!this.invoiceForm.city.trim()) {
        errors.city = true
      }
      if (!this.invoiceForm.province.trim()) {
        errors.province = true
      }
      if (!this.invoiceForm.postalCode.trim()) {
        errors.postalCode = true
      }
      if (!this.invoiceForm.country.trim()) {
        errors.country = true
      }
      this.formErrors = errors
      return Object.keys(errors).length === 0
    },
    closeInvoiceModal() {
      this.showInvoiceModal = false
      this.selectedInvoice = null
    },
    async confirmDownloadInvoice() {
      if (!this.selectedInvoice) return
      
      if (!this.validateForm()) {
        return
      }
      
      try {
        var invoiceData = {
          fullName: this.invoiceForm.fullName,
          email: this.invoiceForm.email,
          company: this.invoiceForm.company,
          taxId: this.invoiceForm.taxId,
          addressLine1: this.invoiceForm.addressLine1,
          addressLine2: this.invoiceForm.addressLine2,
          city: this.invoiceForm.city,
          province: this.invoiceForm.province,
          postalCode: this.invoiceForm.postalCode,
          country: this.invoiceForm.country,
          invoiceRemarks: this.invoiceForm.invoiceRemarks,
          payment_id:this.selectedInvoice.payment_id,
          orderInfo: {
            planName: this.getPlanDisplayName(this.selectedInvoice.planName),
            amount: '$' + this.selectedInvoice.price,
            createTime: this.formatDate(this.selectedInvoice.pay_date)
          }
        }
        
        var response = await generateInvoicePdf(invoiceData)
        
        if (response.code === 200 && response.url) {
          var link = document.createElement('a')
          link.href = response.url
          link.download = 'invoice_' + this.selectedInvoice.payment_id + '.pdf'
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.closeInvoiceModal()
        }
      } catch (error) {
        console.error('Failed to download invoice:', error)
      }
    },
    async cancelSubscription() {
      try {
        var response = await paySubscription(2)
        if (response.code === 200) {
          this.fetchUserInfo()
        }
      } catch (error) {
        console.error('Failed to cancel subscription:', error)
      }
    },
    async restoreSubscription() {
      try {
        var response = await paySubscription(1)
        if (response.code === 200) {
          this.fetchUserInfo()
        }
      } catch (error) {
        console.error('Failed to restore subscription:', error)
      }
    },
    getPayTypeName(payType, payMode) {
      if (payMode === '2') {
        return this.$t('profile.billing.oneTime')
      }
      return payType === 1 ? this.$t('profile.billing.yearly') : this.$t('profile.billing.monthly')
    },
    async fetchUserInfo() {
      this.loading = true
      try {
        var result = await getCurrentUser()
        if (result && result.success && result.user) {
          this.balance = result.user.credits || 0
          this.lastPay = result.user.lastPay || null
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error)
      } finally {
        this.loading = false
      }
    },
    async loadPaymentHistory(page) {
      this.paymentLoading = true
      try {
        var response = await getPaymentHistory(page, this.pageSize)
        if (response && response.code === 200) {
          var filteredList = (response.payList || []).filter(function(item) {
            return item.state === 'finished'
          })
          this.paymentList = filteredList
          this.currentPage = response.currentPageNum || 1
          // 根据过滤后的数量重新计算总页数
          // 如果当前页过滤后为空，尝试加载下一页
          if (filteredList.length === 0 && (response.currentPageNum || 1) < (response.totalPage || 1)) {
            this.loadPaymentHistory((response.currentPageNum || 1) + 1)
            return
          }
          // 如果没有数据，totalPage 设为 1
          if (filteredList.length === 0) {
            this.totalPage = 1
          } else {
            this.totalPage = response.totalPage || 1
          }
        }
      } catch (error) {
        console.error('Failed to load payment history:', error)
      } finally {
        this.paymentLoading = false
      }
    }
  },
  mounted() {
    if (!this.user) {
      this.$router.push('/')
      return
    }
    this.balance = this.user.credits || 0
    this.lastPay = this.user.lastPay || null
    this.fetchUserInfo()
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 40px 20px;
}

.profile-container {
  max-width: 800px;
  margin: 0 auto;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-avatar-default {
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  color: #000;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.profile-email {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 8px 0;
}

.profile-join-time {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
}

.profile-tabs {
  display: flex;
  gap: 8px;
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 6px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.tab-btn.active {
  background: var(--accent);
  color: #000;
}

.profile-content {
  margin-bottom: 24px;
}

.card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  margin-bottom: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 24px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
}

.card-body {
  padding: 24px;
}

.balance-info {
  text-align: center;
}

.balance-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.balance-value {
  font-size: 36px;
  font-weight: 700;
  color: var(--accent);
}

.upgrade-card {
  text-align: center;
  padding: 40px 24px;
}

.upgrade-icon {
  color: var(--accent);
  margin-bottom: 16px;
}

.upgrade-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.upgrade-card p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
}

.upgrade-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.upgrade-btn:hover {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
}

.empty-text {
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

.privacy-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-color);
}

.privacy-item:last-child {
  border-bottom: none;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--bg-secondary);
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: var(--text-primary);
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--accent);
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.support-card .card-body {
  text-align: center;
}

.support-body p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
}

.support-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  margin-bottom: 12px;
}

.support-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.support-note {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
}

.privacy-card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  padding: 32px;
  text-align: center;
}

.privacy-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.privacy-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.privacy-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.privacy-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--text-primary);
  font-size: 14px;
  border-bottom: 1px solid var(--border-color);
}

.privacy-list li:last-child {
  border-bottom: none;
}

.privacy-list li svg {
  color: var(--accent);
  flex-shrink: 0;
}

.loading-text {
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
  padding: 20px 0;
}

.payment-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.payment-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-plan {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.payment-type {
  font-size: 12px;
  color: var(--accent);
  background: rgba(0, 217, 126, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  width: fit-content;
}

.payment-date {
  font-size: 12px;
  color: var(--text-muted);
}

.payment-amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.page-btn {
  padding: 8px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--text-secondary);
}

.billing-card .card-header {
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 768px) {
  .billing-card .card-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.billing-header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.billing-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.billing-title svg {
  color: var(--text-secondary);
}

.billing-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.billing-body {
  padding: 0;
}

.billing-empty {
  padding: 24px;
}

.billing-table-wrapper {
  overflow-x: auto;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin: 0 24px 24px 24px;
}

.billing-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.billing-table thead {
  background: var(--bg-secondary);
  position: sticky;
  top: 0;
}

.billing-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
}

.billing-table th.text-right {
  text-align: right;
}

.billing-table td {
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.billing-table tbody tr:last-child td {
  border-bottom: none;
}

.billing-table tbody tr:hover {
  background: var(--bg-secondary);
}

.plan-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.plan-type {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
}

.date-cell {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.amount-cell {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
}

.invoice-cell {
  text-align: right;
}

.invoice-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.invoice-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.invoice-btn svg {
  flex-shrink: 0;
}

.subscription-card .card-body {
  padding: 24px;
}

.subscription-info {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
}

.subscription-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.subscription-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.subscription-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subscription-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-value.status-active {
  color: var(--accent);
}

.subscription-value.status-cancel {
  color: #f97316;
}

.subscription-value.status-pending {
  color: #3b82f6;
}

.subscription-value.status-fail {
  color: #ef4444;
}

.subscription-value svg {
  flex-shrink: 0;
}

.subscription-actions {
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.subscription-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.subscription-btn:hover {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
}

.subscription-btn svg {
  flex-shrink: 0;
}

.subscription-cancel {
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
  margin-top: 16px;
}

.cancel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
}

.cancel-btn svg {
  flex-shrink: 0;
}

.cancel-note {
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
  margin: 8px 0 0 0;
  line-height: 1.5;
}

.cancel-warning {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.cancel-warning p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

.cancel-warning strong {
  color: var(--accent);
}

.restore-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 8px;
  color: #000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.restore-btn:hover {
  opacity: 0.9;
}

.restore-btn svg {
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .subscription-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
}

.invoice-modal {
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.invoice-modal .modal-body {
  overflow-y: auto;
  flex: 1;
}

.invoice-section {
  margin-bottom: 24px;
}

.invoice-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.invoice-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group.full {
  grid-column: span 2;
}

.form-group label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.form-group input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--accent);
}

.form-group input::placeholder {
  color: var(--text-muted);
}

.form-group .required {
  color: #ef4444;
  font-weight: 500;
}

.form-group .optional {
  color: var(--text-muted);
  font-weight: 400;
  font-size: 11px;
}

.form-group.has-error input {
  border-color: #ef4444;
}

.form-group.has-error input:focus {
  border-color: #ef4444;
}

.error-text {
  font-size: 11px;
  color: #ef4444;
  margin-top: 2px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.invoice-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.invoice-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invoice-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.invoice-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.invoice-id {
  font-family: monospace;
  font-size: 12px;
  background: var(--bg-secondary);
  padding: 4px 8px;
  border-radius: 4px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border-color);
}

.modal-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.secondary {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.modal-btn.secondary:hover {
  background: var(--bg-primary);
  border-color: var(--text-secondary);
}

.modal-btn.primary {
  background: var(--accent);
  border: 1px solid var(--accent);
  color: #000;
}

.modal-btn.primary:hover {
  opacity: 0.9;
}

.modal-btn svg {
  flex-shrink: 0;
}
</style>
